import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { TabsContext, panelId, tabId } from "./tabs-context.js";

/** A panel's padding, on a 12/18/24px ramp above its content. */
export type TabPanelPadding = "none" | "sm" | "md" | "lg" | "inset";

/* `sm` is today's pt-3. `inset` lines the panel up with an `inset` tablist:
   the same 20px sides (14px below 460px), 18px above and 24px below. Literal
   class strings so Tailwind sees them. */
const PANEL_PADDING: Record<TabPanelPadding, string> = {
  none: "",
  sm: "pt-3",
  md: "pt-4.5",
  lg: "pt-6",
  inset: "px-5 pt-4.5 pb-6 max-[460px]:px-3.5",
};

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  /** Keep the panel mounted (and merely hidden) when inactive. Default: unmount. */
  keepMounted?: boolean;
  /**
   * Space around the panel's content. `sm` (12px above) is the default;
   * `md` is 18px and `lg` 24px above; `none` is no padding. `inset` pads the
   * sides as well, to line up with a `<Tabs inset>` tablist.
   */
  padding?: TabPanelPadding;
  /**
   * Layout only: width, flex, overflow and margin. Never padding — that is
   * the `padding` option.
   */
  className?: string | undefined;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  function TabPanel(
    {
      value,
      keepMounted = false,
      padding = "sm",
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const ctx = React.useContext(TabsContext);
    if (!ctx) throw new Error("TabPanel must be used within <Tabs>");
    const active = ctx.value === value;
    const rendered = active || keepMounted;
    const registerPanel = ctx.registerPanel;
    React.useEffect(() => {
      if (!rendered) return;
      return registerPanel(value);
    }, [rendered, registerPanel, value]);
    if (!rendered) return null;
    return (
      <div
        /* The panel ARRIVES; it used to appear (ADR-0091). The indicator above
           slides, and the content under it swapped in a single frame — one
           component moving by two different rules. `key` is the ACTIVE value,
           not this panel's own, so the host pattern that renders ONE TabPanel
           whose `value` follows the selection still gets a fresh element for
           `@starting-style` to fade and rise from; per-value panels already
           mount fresh and the key is constant for them.

           Only the incoming panel animates. There is deliberately no exit:
           two panels on screen at once would stack their heights and the
           region would jump by the taller one's worth mid-transition.

           `keepMounted` keys by its OWN value instead, so it never remounts —
           a panel kept mounted is asking to keep its state (a half-typed
           draft, a scroll position), and remounting it on every switch is
           precisely what it is asking not to happen. It trades the arrival for
           that, which is the trade it was opted into. */
        key={keepMounted ? value : ctx.value}
        ref={ref}
        role="tabpanel"
        id={panelId(ctx.baseId, value)}
        aria-labelledby={tabId(ctx.baseId, value)}
        tabIndex={0}
        hidden={!active}
        className={cn(
          "pb-motion-content-enter",
          PANEL_PADDING[padding],
          "focus-visible:outline-none",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
